import type { GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { ParameterValue } from "building-types/options/parameter";
import type { ElevationAnnotationParams, ElevationDebugOptions } from "building-types/views/elevationAnnotationTypes";
import type { ElevationReferenceFrame } from "building-types/views/elevationReferenceFrame";
import type { GradeLineSource } from "building-types/views/GradeLineSource";
import type { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { DimensionPrecision } from "building-types/views/viewTypes";
import type { Table } from "common/data/table";
import type { SheetGeometrySelectorEffectMap } from "doc/plans/SheetGeometrySelectorEffectMap";
import type { Vec3 } from "geom/vec3";
import type { SheetGeometryStyler } from "./planGeometrySourceTypes";

/**
 * Selects the occlusion engine used to compute elevation auto-annotations:
 * - "hlr-wasm": the WASM Hidden Line Removal path (production default)
 * - "wasm": the WASM BSP path
 * - "js": the deprecated JS BSP path
 */
export type ElevationOcclusionEngine = "wasm" | "hlr-wasm" | "js";

/**
 * Resolves the occlusion engine for a building from the `hiddenLineRemoval` feature flag.
 */
export function elevationOcclusionEngineForFeatures(
  enabledFeatures: Pick<GenerateBuildingFeatureSet, "hiddenLineRemoval">,
): ElevationOcclusionEngine {
  return enabledFeatures.hiddenLineRemoval === false ? "wasm" : "hlr-wasm";
}

export interface ElevationAnnotationCommonParams {
  readonly eyePos: Vec3;
  readonly referenceFrame: ElevationReferenceFrame;
  readonly includeGradeLine: boolean;
  readonly gradeLineSource?: GradeLineSource;
  readonly allowBelowGroundAnnotations: boolean;
  readonly elevationDebugOptions: ElevationDebugOptions;
  readonly textScalingFactor: number;
  readonly dimensionPrecision?: DimensionPrecision;
  readonly elevationAnnotationParams?: ElevationAnnotationParams;
  readonly noSettings?: boolean;
  // Used to measure the text geometry for the automatic elevation annotation placement.
  // This isn't meant for general use when we're generating sheet geometries.
  readonly sheetGeometryStyler?: SheetGeometryStyler;
  // Generated geometry in these categories is returned normally, but does not reserve annotation space.
  readonly categoriesToExcludeFromCollisions?: readonly SheetGeometryCategory[];
  // Needed for elevation viewport scaling.
  readonly viewportScale?: number;
  readonly viewportOrViewName?: string;
  readonly sheetName?: string;

  readonly visibilitySelectors?: SheetGeometrySelectorEffectMap[];

  readonly globalParameterValues?: Table<ParameterValue>;

  readonly occlusionEngineOverride?: ElevationOcclusionEngine;

  // When set, the viewport is filtered to this reporting unit. Elevation auto-annotations are
  // building-wide and not attributable to a single unit, so they are suppressed entirely rather than
  // shown across the whole building.
  readonly selectedReportingUnitLabelId?: string;
}
